// src/pages/earningsImport/pos/posSaver.js

import { db } from "../../../firebase/firebase";

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

import { getAssignmentForDate } from "../../../utils/posAssignmentHistory";

// Performance caches
const assignmentCache = new Map();
const vehicleCache = new Map();

const normalizeTerminalId = (value) =>
  String(value ?? "").replace(/\.0$/, "").trim();

const normalizeDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value?.toDate === "function") {
    value = value.toDate();
  }

  const parsed = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const cleanText = (value) => String(value ?? "").trim();

const cleanAmount = (value) =>
  Number(Number(value || 0).toFixed(2));

// Stored as a FIELD only.
// Firestore document IDs remain automatic/random.
export function makePOSTransactionKey(tx) {
  return [
    normalizeTerminalId(tx?.terminalId),
    normalizeDate(tx?.date),
    cleanText(tx?.txnTime),
    cleanText(tx?.authId),
    cleanText(tx?.rrn),
    cleanText(tx?.arn),
    cleanText(tx?.cardNumber),
    cleanAmount(tx?.amount).toFixed(2),
  ].join("|");
}

// Used only for older records that do not have transactionKey.
function makeLegacySignature(tx) {
  return [
    normalizeTerminalId(tx?.terminalId),
    normalizeDate(tx?.date),
    cleanAmount(tx?.amount).toFixed(2),
    cleanText(tx?.carId),
  ].join("|");
}

async function fastGetAssignment(terminalId, date) {
  const terminal = normalizeTerminalId(terminalId);
  const normalizedDate = normalizeDate(date);

  const key = `${terminal}|${normalizedDate}`;

  if (!terminal || !normalizedDate) {
    return null;
  }

  if (assignmentCache.has(key)) {
    return assignmentCache.get(key);
  }

  const assignment = await getAssignmentForDate(
    terminal,
    normalizedDate
  );

  assignmentCache.set(key, assignment);

  return assignment;
}

async function fastGetVehicle(vehicleId) {
  if (!vehicleId) {
    return null;
  }

  if (vehicleCache.has(vehicleId)) {
    return vehicleCache.get(vehicleId);
  }

  const snap = await getDoc(
    doc(db, "vehicles", vehicleId)
  );

  const data = snap.exists() ? snap.data() : null;

  vehicleCache.set(vehicleId, data);

  return data;
}

/**
 * Existing Firestore structure is preserved:
 *
 * posUploads/{month}/records/{automaticRecordId}
 *
 * posUploads/{month}/uploads/{automaticUploadId}/transactions/{automaticAuditId}
 *
 * For each new transaction, the audit row and monthly record
 * are committed in one atomic write batch.
 *
 * Both documents are created together or neither is.
 */
export async function savePOSTransactions(
  month,
  rawTransactions,
  file
) {
  if (
    !month ||
    !Array.isArray(rawTransactions) ||
    rawTransactions.length === 0
  ) {
    return {
      ok: false,
      savedCount: 0,
      duplicateCount: 0,
      skippedCount: 0,
      unmatchedCount: 0,
      errors: ["Missing month or transactions"],
    };
  }

  const normalizedMonth = String(month).trim();

  const monthRef = doc(
    db,
    "posUploads",
    normalizedMonth
  );

  await setDoc(
    monthRef,
    {
      month: normalizedMonth,
      updatedAt: serverTimestamp(),
    },
    {
      merge: true,
    }
  );

  const uploadRef = await addDoc(
    collection(monthRef, "uploads"),
    {
      uploadedAt: serverTimestamp(),
      fileName: file?.name || "",
      originalSize: rawTransactions.length,
      type: "pos",
      status: "processing",
      month: normalizedMonth,
    }
  );

  const uploadTransactionsRef = collection(
    uploadRef,
    "transactions"
  );

  const recordsRef = collection(
    monthRef,
    "records"
  );

  /*
   * Read existing records once.
   *
   * New-format records use transactionKey.
   * Only genuine older records without transactionKey
   * use the legacy signature.
   */
  const existingRecordsSnap = await getDocs(
    recordsRef
  );

  const existingTransactionKeys = new Set();
  const existingLegacySignatureCounts = new Map();

  existingRecordsSnap.docs.forEach((recordDoc) => {
    const data = recordDoc.data();

    if (data?.transactionKey) {
      existingTransactionKeys.add(
        String(data.transactionKey)
      );
    } else {
      const signature = makeLegacySignature(data);
      existingLegacySignatureCounts.set(
        signature,
        (existingLegacySignatureCounts.get(signature) || 0) + 1
      );
    }
  });

  /*
   * Some bank POS exports do not contain a transaction time/auth/RRN/card
   * reference for every row. In that case two genuine transactions can have
   * exactly the same terminal + date + amount (and therefore the same modern
   * transaction key). Keep an occurrence counter for the CURRENT source file
   * so the 2nd/3rd identical source row gets a stable suffix instead of being
   * silently discarded.
   *
   * Occurrence #1 intentionally keeps the old transactionKey unchanged.
   * This is backward-compatible: re-uploading a file after this fix will skip
   * all already-saved first occurrences and save only any previously-missed
   * 2nd/3rd identical rows.
   */
  const currentBaseKeyOccurrences = new Map();
  const currentLegacySignatureOccurrences = new Map();

  let savedCount = 0;
  let duplicateCount = 0;
  let skippedCount = 0;

  const unmatched = [];
  const duplicates = [];
  const errors = [];

  for (const tx of rawTransactions) {
    const terminalId = normalizeTerminalId(
      tx?.terminalId
    );

    const date = normalizeDate(tx?.date);

    const amount = cleanAmount(tx?.amount);

    if (!date || !terminalId) {
      skippedCount += 1;

      unmatched.push({
        terminalId:
          terminalId ||
          String(tx?.terminalId ?? ""),
        date:
          date ||
          String(tx?.date ?? ""),
        amount,
        reason: "Missing date or terminalId",
      });

      continue;
    }

    try {
      const assignment =
        await fastGetAssignment(
          terminalId,
          date
        );

      if (!assignment?.vehicleId) {
        skippedCount += 1;

        unmatched.push({
          terminalId,
          date,
          amount,
          reason:
            "No assignment match (posDeviceAssignments)",
        });

        continue;
      }

      const vehicleId = assignment.vehicleId;

      const deviceCompany =
        assignment.deviceCompany || "";

      const car =
        await fastGetVehicle(vehicleId);

      const carNumber =
        car?.vehicleNumber || "UNKNOWN";

      const carCompany =
        car?.company ||
        tx?.company ||
        "";

      const vehicleType =
        car?.vehicleType || "";

      const day = Number(
        String(date).split("-")[2] || 0
      );

      const week =
        day <= 7
          ? 1
          : day <= 14
          ? 2
          : day <= 21
          ? 3
          : day <= 28
          ? 4
          : 5;

      const settlementRequired = Boolean(
        deviceCompany &&
          carCompany &&
          deviceCompany !== carCompany
      );

      /*
       * Exact modern transaction key.
       */
      const baseTransactionKey =
        makePOSTransactionKey({
          ...tx,
          terminalId,
          date,
          amount,
        });

      const transactionOccurrence =
        (currentBaseKeyOccurrences.get(baseTransactionKey) || 0) + 1;

      currentBaseKeyOccurrences.set(
        baseTransactionKey,
        transactionOccurrence
      );

      const transactionKey =
        transactionOccurrence === 1
          ? baseTransactionKey
          : `${baseTransactionKey}|occ:${transactionOccurrence}`;

      /*
       * Used only to compare against genuinely
       * older Firestore records that do not have
       * transactionKey. Legacy rows are counted,
       * not treated as a simple yes/no signature,
       * so one old row cannot suppress two genuine
       * identical transactions from the source file.
       */
      const legacySignature =
        makeLegacySignature({
          terminalId,
          date,
          amount,
          carId: vehicleId,
        });

      const legacyOccurrence =
        (currentLegacySignatureOccurrences.get(legacySignature) || 0) + 1;

      currentLegacySignatureOccurrences.set(
        legacySignature,
        legacyOccurrence
      );

      const existingLegacyCount =
        existingLegacySignatureCounts.get(legacySignature) || 0;

      const duplicate =
        existingTransactionKeys.has(transactionKey) ||
        legacyOccurrence <= existingLegacyCount;

      /*
       * Keep a complete audit-history row even
       * when monthly record is a duplicate.
       */
      if (duplicate) {
        const duplicateAuditRef = doc(
          uploadTransactionsRef
        );

        await setDoc(
          duplicateAuditRef,
          {
            transactionKey,
            baseTransactionKey,
            transactionOccurrence,
            uploadId: uploadRef.id,
            month: normalizedMonth,
            sourceFileName:
              file?.name || "",
            date,
            terminalId,
            amount,
            txnTime: cleanText(
              tx?.txnTime
            ),
            authId: cleanText(
              tx?.authId
            ),
            arn: cleanText(tx?.arn),
            rrn: cleanText(tx?.rrn),
            cardNumber: cleanText(
              tx?.cardNumber
            ),
            week,
            carId: vehicleId,
            carNumber,
            carCompany,
            deviceCompany,
            settlementRequired,
            vehicleType,
            saveStatus: "duplicate",
            duplicateOfExistingRecord: true,
            createdAt: serverTimestamp(),
          }
        );

        duplicateCount += 1;

        duplicates.push({
          transactionKey,
          terminalId,
          date,
          amount,
        });

        continue;
      }

      /*
       * Pre-create automatic Firestore refs.
       * IDs remain random as before.
       */
      const auditRef = doc(
        uploadTransactionsRef
      );

      const recordRef = doc(
        recordsRef
      );

      const commonData = {
        transactionKey,
        baseTransactionKey,
        transactionOccurrence,
        uploadId: uploadRef.id,
        month: normalizedMonth,
        sourceFileName:
          file?.name || "",
        date,
        terminalId,
        amount,
        txnTime: cleanText(
          tx?.txnTime
        ),
        authId: cleanText(
          tx?.authId
        ),
        arn: cleanText(tx?.arn),
        rrn: cleanText(tx?.rrn),
        cardNumber: cleanText(
          tx?.cardNumber
        ),
        week,
        carId: vehicleId,
        carNumber,
        carCompany,
        deviceCompany,
        settlementRequired,
        vehicleType,
        createdAt: serverTimestamp(),
      };

      /*
       * Audit row and monthly POS record
       * are committed atomically.
       */
      const batch = writeBatch(db);

      batch.set(
        auditRef,
        {
          ...commonData,
          recordId: recordRef.id,
          saveStatus: "saved",
        }
      );

      batch.set(
        recordRef,
        {
          ...commonData,
          auditTransactionId:
            auditRef.id,
        }
      );

      await batch.commit();

      /*
       * Add only the exact occurrence-aware modern key.
       * Do not add/increment the legacy signature count here.
       * That preserves every source-row occurrence while still
       * making a complete re-upload idempotent.
       */
      existingTransactionKeys.add(
        transactionKey
      );

      savedCount += 1;
    } catch (error) {
      skippedCount += 1;

      errors.push(
        `Failed to save tx terminalId=${terminalId} date=${date} amount=${amount}: ${
          error?.message || error
        }`
      );
    }
  }

  const summary = {
    status:
      errors.length
        ? "done_with_errors"
        : "done",

    savedCount,
    duplicateCount,
    skippedCount,
    unmatchedCount: unmatched.length,
    errorCount: errors.length,
    finishedAt: serverTimestamp(),

    sampleUnmatched:
      unmatched.slice(0, 30),

    sampleDuplicates:
      duplicates.slice(0, 30),

    errors:
      errors.slice(0, 30),
  };

  await updateDoc(
    uploadRef,
    summary
  );

  return {
    ok:
      errors.length === 0 &&
      (
        savedCount > 0 ||
        duplicateCount > 0
      ),

    savedCount,
    duplicateCount,
    skippedCount,
    unmatchedCount:
      unmatched.length,

    unmatched,
    duplicates,
    errors,

    uploadId:
      uploadRef.id,
  };
}